import api from './api';
import { toQueryDateYMD } from '../utils/formatters';
import { remitoDescuentaStock, TIPO_REMITO_COMPRA } from '../utils/remitoDescuentaStock';
import { asPdfBlobOrThrow } from '../utils/apiError';

export interface RemitoCompraDetalle {
  nroIdformulario: number;
  nroItem: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad?: number;
  precioUnitario?: number;
  descuento?: number;
  subtotal?: number;
  notas?: string;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
}

export interface RemitoCompra {
  nroFormulario: number;
  nroRemito?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string | Date;
  horaEmision?: string | Date;
  nroTipoFormulario?: string;
  tipoCliente?: string;

  // A nivel de modelo se usa NroCliente, pero representa al proveedor en remitos de compra.
  nroCliente?: number;
  nombre?: string;
  nroLista?: string;
  codigoMoneda?: number;
  importeTotal?: number;
  asentado?: string;
  descripcion?: string;
  usuario?: string;

  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;

  nroUbicacionDestino?: number;
  nroFormularioFactura?: number | null;
  detalles?: RemitoCompraDetalle[];
}

export interface RemitoCompraSearchParams {
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  fechaDesde?: Date | string;
  fechaHasta?: Date | string;
  nroProveedor?: string;
  asentado?: string;
  nroSuc?: number;
}

export interface RemitoCompraPagedResult {
  items: RemitoCompra[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface RemitoCompraFormularioDto {
  descripcion?: string;
  nroSuc?: number;
  letra?: string;
  nroComprobante?: number;
  fechaEmision?: string | Date;
  horaEmision?: string | Date;
  fechaVencim?: string | Date;

  // Remito compra: requerido (proveedor)
  nroProveedor: number;

  nombre?: string;
  cuit?: string;

  // Depósito/ubicación destino del stock
  nroUbicacionDestino?: number;
  nroFormularioFactura?: number | null;
}

export interface RemitoCompraCreateItemDto {
  codigoArticulo: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc: number;
  notas?: string;

  // Campos opcionales, soportados por el backend
  um?: string;
  codigoMoneda?: number;
  cotizacion?: number;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  lotes?: import('./loteService').LoteAsignacion[];
}

export interface RemitoCompraCreatePayload {
  formulario: RemitoCompraFormularioDto;
  items: RemitoCompraCreateItemDto[];
}

export interface AnularRemitoCompraDto {
  motivo?: string;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function mapRemitoCompraItemToDto(item: {
  codigoArticulo: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc?: number;
  um?: string;
  notas?: string;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  lotes?: import('./loteService').LoteAsignacion[];
}): RemitoCompraCreateItemDto {
  const dto: RemitoCompraCreateItemDto = {
    codigoArticulo: item.codigoArticulo.trim(),
    cantidad: Number(item.cantidad),
    precioUnitario: Number(item.precioUnitario),
    porcDesc: Number(item.porcDesc ?? 0),
  };
  const desc = item.descripcion?.trim();
  if (desc) dto.descripcion = desc;
  const um = (item.um || '').trim();
  if (um) dto.um = um;
  const notas = item.notas?.trim();
  if (notas) dto.notas = notas.slice(0, 500);
  if (
    item.nroFormularioMaster != null &&
    item.nroItemMaster != null &&
    item.nroFormularioMaster > 0 &&
    item.nroItemMaster > 0
  ) {
    dto.nroFormularioMaster = item.nroFormularioMaster;
    dto.nroItemMaster = item.nroItemMaster;
  }
  if (item.lotes && item.lotes.length > 0) {
    dto.lotes = item.lotes;
  }
  return dto;
}

const remitoCompraService = {
  getRemitosCompra: async (params: RemitoCompraSearchParams): Promise<RemitoCompraPagedResult> => {
    const queryParams: Record<string, unknown> = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
      SearchTerm: params.searchTerm || undefined,
      NroProveedor: params.nroProveedor || undefined,
      Asentado: params.asentado || undefined,
      NroSuc: params.nroSuc ?? undefined,
    };

    if (params.fechaDesde) {
      queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
    }
    if (params.fechaHasta) {
      queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
    }

    const response = await api.get<RemitoCompraPagedResult>('/api/RemitosCompra', { params: queryParams });
    return response.data;
  },

  getRemitoCompra: async (id: number): Promise<RemitoCompra> => {
    const response = await api.get<RemitoCompra>(`/api/RemitosCompra/${id}`);
    return response.data;
  },

  createRemitoCompra: async (payload: RemitoCompraCreatePayload): Promise<RemitoCompra> => {
    await remitoDescuentaStock(TIPO_REMITO_COMPRA);
    const maxAttempts = 3;
    let lastError: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const response = await api.post<RemitoCompra>('/api/RemitosCompra', payload);
        return response.data;
      } catch (error) {
        lastError = error;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 409 && attempt < maxAttempts - 1) {
          await sleep(350 * (attempt + 1));
          continue;
        }
        throw error;
      }
    }
    throw lastError;
  },

  updateRemitoCompra: async (id: number, payload: RemitoCompraCreatePayload): Promise<void> => {
    await api.put(`/api/RemitosCompra/${id}`, payload);
  },

  anularRemitoCompra: async (id: number, dto?: AnularRemitoCompraDto): Promise<void> => {
    await api.post(`/api/RemitosCompra/${id}/anular`, dto ?? {});
  },

  getEtiquetasCodigosPdf: async (
    nroFormulario: number,
    items: Array<{ codigoArticulo: string; copias: number }>,
    idCompania?: string,
  ): Promise<Blob> => {
    const response = await api.post(
      `/api/RemitosCompra/${nroFormulario}/etiquetas-codigos-pdf`,
      { items },
      { params: { idCompania: idCompania?.trim() || undefined }, responseType: 'blob' },
    );
    return asPdfBlobOrThrow(response.data, 'El servidor no pudo generar las etiquetas');
  },

  getEtiquetasCodigosZpl: async (
    nroFormulario: number,
    items: Array<{ codigoArticulo: string; copias: number }>,
    idCompania?: string,
  ): Promise<string> => {
    const response = await api.post<string>(
      `/api/RemitosCompra/${nroFormulario}/etiquetas-codigos-zpl`,
      { items },
      { params: { idCompania: idCompania?.trim() || undefined }, responseType: 'text' },
    );
    const zpl = typeof response.data === 'string' ? response.data.trim() : '';
    if (!zpl) throw new Error('El servidor no pudo generar el ZPL de etiquetas');
    return zpl;
  },
};

export default remitoCompraService;

